
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from '@/contexts/LanguageContext';
import { translateCategoryName } from '@/utils/contentTranslation';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface Category {
  id: string;
  name: string;
  image: string;
  parent_id: string | null;
}

const Categories = () => {
  const { t, currentLanguage } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .is('parent_id', null) // Somente categorias principais
          .order('name');
        
        if (error) throw error;
        
        setCategories(data || []);
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Imagem padrão para categorias sem imagem
  const defaultImage = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 bg-brand-darkest">
      <div className="container mx-auto px-4">
        <h2 className={`section-heading transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('categories.title')}</h2>
        <p className={`section-subheading transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('categories.subtitle')}</p>
        
        {loading ? (
          // Estado de carregamento
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 my-8">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg overflow-hidden bg-brand-dark h-40 md:h-64">
                <Skeleton className="h-full w-full bg-gray-700" />
              </div>
            ))}
          </div>
        ) : categories.length > 0 ? (
          // Exibir categorias quando carregadas - Grid em mobile, Carousel em desktop
          <>
            {/* Mobile: Grid 2x2 */}
            <div className="md:hidden grid grid-cols-2 gap-3 my-8">
              {categories.slice(0, 4).map((category) => (
                <div key={category.id} className="hover-scale">
                  <div className="rounded-lg overflow-hidden bg-brand-dark relative group h-40">
                    <img
                      src={category.image || defaultImage}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = defaultImage;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-2">
                      <h3 className="text-white font-bold text-xs mb-1">{translateCategoryName(category.name, currentLanguage)}</h3>
                      <Button asChild size="sm" className="bg-brand-red hover:bg-brand-red/90 text-white w-full text-xs py-1 h-7">
                        <Link to={`/shop?category=${category.id}`}>
                          {t('categories.viewProducts')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Carousel */}
            <Carousel
              className={`hidden md:block my-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
              opts={{
                align: "start",
                loop: true,
              }}
            >
              <CarouselContent>
                {categories.map((category, index) => (
                  <CarouselItem key={category.id} className="md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <div className="hover-scale">
                      <div className="rounded-lg overflow-hidden bg-brand-dark relative group h-64 mx-2">
                        <img
                          src={category.image || defaultImage}
                          alt={category.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = defaultImage;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                          <h3 className="text-white font-bold">{translateCategoryName(category.name, currentLanguage)}</h3>
                          <Button asChild className="bg-brand-red hover:bg-brand-red/90 text-white w-full mt-3 hover-glow">
                            <Link to={`/shop?category=${category.id}`}>
                              {t('categories.viewProducts')}
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </>
        ) : (
          // Mensagem quando não há categorias
          <div className="text-center py-8">
            <p className="text-gray-400">{t('categories.noCategoriesFound')}</p>
          </div>
        )}
        
        <div className={`flex justify-center mt-8 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Button asChild className="bg-brand-red hover:bg-brand-red/90 text-white hover-glow">
            <Link to="/categories">
              {t('categories.viewAllCategories')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Categories;
